import { useState, useMemo } from "react";
import { useQuery, type WatchQueryFetchPolicy } from "@apollo/client";
import { GET_FIELD_VALUES } from "../../ManualFindings/ManualFindingsApi";
import { cloneDeep } from "lodash-es";
import type { FiltersConfig } from "../../../Rules/RulesDraft/types";

export interface FindingFilterData {
  // GraphQL scalar with two real runtime shapes: an array of filter values on
  // the normal path, or a parsed object (`Record<string, unknown>`) on the
  // `withCoverage` path below. The union covers both; array consumers narrow
  // via cast and the object consumer reads it with `Object.entries`.
  values: Record<string, unknown> | unknown[];
  field?: string;
  total_count?: number;
  values_with_coverage?: string;
}

export interface FieldValuesData {
  finding_filter_data: FindingFilterData;
}

export interface UseFilterFieldValuesReturn {
  valueSearchInput: string;
  setValueSearchInput: React.Dispatch<React.SetStateAction<string>>;
  fieldOptions: FieldValuesData | null;
  loading: boolean;
}

const useFilterFieldValues = (
  field: string,
  getInitialValues: boolean,
  minChars: number,
  skip: boolean,
  // Callers pass `null` for "no explicit limit"; `limit || (...)` below treats
  // null and undefined identically at runtime, so accept both.
  limit?: number | null,
  withTotalCount = false,
  fetchPolicy: WatchQueryFetchPolicy = "cache-first",
  filtersConfig: FiltersConfig | null = null,
  withCoverage = false
): UseFilterFieldValuesReturn => {
  const [valueSearchInput, setValueSearchInput] = useState("");

  const { data: fieldOptionsData, loading } = useQuery<FieldValuesData>(
    GET_FIELD_VALUES,
    {
      variables: {
        filterName: field,
        contains: valueSearchInput,
        limit: limit || (valueSearchInput ? 10 : 20),
        with_total_count: withTotalCount,
        filters_config: filtersConfig,
        with_coverage: withCoverage,
      },
      fetchPolicy: fetchPolicy,
      skip: (!getInitialValues && valueSearchInput?.length < minChars) || skip,
    }
  );

  const fieldOptions = useMemo<FieldValuesData | null>(() => {
    if (!fieldOptionsData) {
      return null;
    }

    if (!withCoverage) {
      return fieldOptionsData;
    }
    const valuesWithCoverage =
      fieldOptionsData?.finding_filter_data?.values_with_coverage;

    let valuesWithCoverageParsed: Record<string, unknown> = {};
    try {
      // Preserve original runtime: when `valuesWithCoverage` is falsy the
      // fallback was an object literal (`{}`), not a string. JSON.parse coerces
      // it to "[object Object]" and throws (handled below). Cast keeps that
      // exact behavior; tightening the fallback to "{}" would change it.
      valuesWithCoverageParsed = JSON.parse(
        valuesWithCoverage || ({} as unknown as string)
      );
    } catch (error) {
      console.error("error parsing valuesWithCoverage", error);
    }

    const newFieldOptionsData = cloneDeep(fieldOptionsData);
    newFieldOptionsData.finding_filter_data.values = valuesWithCoverageParsed;
    return newFieldOptionsData;
  }, [fieldOptionsData]);

  return {
    valueSearchInput,
    setValueSearchInput,
    fieldOptions,
    loading,
  };
};

export default useFilterFieldValues;
